// Game settings → Keys (spec/dashboard.md section 12.4): the game's service keys (`manage_keys` reads and writes them),
// bound to the whole game, to one environment and / or to one branch (M7 slice 3).
import { ops } from '../../api/operations';
import type { ServiceKey } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { queryCache, useQuery } from '../../workspace/cache';
import { KeysPanel } from './KeysPanel';
import { LoadError } from '../../ui/PageStates';
import { Lock, Plus } from '../../ui/icons';

export function KeysTab() {
  const { org, game, environments, branches, can, missing, offsets } = useGame();
  const { client } = useAuth();
  const [zone] = useTimeZone();
  const allowed = can('manage_keys');
  const key = `game:${org}/${game.slug}:keys`;
  const keysQ = useQuery(allowed ? key : null, () => client.all((cursor) => client.data(ops.listGameServiceKeys, { path: { org, game: game.slug }, query: { limit: 200, cursor } })));
  const reload = () => queryCache.invalidate(key);
  const keyPath = (k: ServiceKey) => ({ org, game: game.slug, key: k.id });

  if (!allowed) {
    return (
      <div data-testid="settings-keys">
        <div className="pane">
          <div className="pane-head">
            <h2>Service keys</h2>
            <div className="spacer" />
            <Button variant="primary" icon={<Plus aria-hidden="true" />} missingPermission={missing('manage_keys')}>
              Create…
            </Button>
          </div>
          <div className="pane-body">
            <p className="muted settings-locked" role="note">
              <Lock className="icon-s" aria-hidden="true" /> You lack the 'manage_keys' permission: the game's service keys are visible to those who manage them.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="settings-keys">
      {keysQ.error ? (
        <LoadError>The service keys could not be read.</LoadError>
      ) : null}
      <KeysPanel
        rows={keysQ.data ?? []}
        loading={keysQ.loading}
        missing={null}
        environments={environments.map((e) => ({ id: e.id, slug: e.slug }))}
        branches={branches.map((b) => ({ id: b.id, name: b.name }))}
        wholeLabel="the whole game"
        when={(v) => formatInstantWithZone(v, zone, { offsets })}
        ctx={{ offsets }}
        onCreate={async (body, idempotencyKey) => {
          const r = await client.data(ops.createGameServiceKey, { path: { org, game: game.slug }, body, idempotencyKey });
          reload();
          return r;
        }}
        onRotate={async (k, graceSeconds) => {
          const r = await client.data(ops.rotateGameServiceKey, { path: keyPath(k), body: { graceSeconds } });
          reload();
          return r;
        }}
        onRevoke={async (k) => {
          await client.call(ops.revokeGameServiceKey, { path: keyPath(k) });
          reload();
        }}
      />
    </div>
  );
}
